import { createFileRoute, Link } from "@tanstack/react-router";
import { SiteHeader } from "@/components/site/SiteHeader";
import { SiteFooter } from "@/components/site/SiteFooter";
import { CiteThis } from "@/components/site/CiteThis";
import { SUSPENSIONS } from "@/lib/reference/suspensions";
import { SITE } from "@/lib/site";

export const Route = createFileRoute("/tools/suspensions/")({
  head: () => {
    const url = `${SITE.url}/tools/suspensions`;
    return {
      meta: [
        { title: `GBP Suspension Reason Lookup — Why Google Suspended Your Profile | ${SITE.name}` },
        {
          name: "description",
          content:
            "The most common Google Business Profile suspension reasons — keyword stuffing, address violations, ineligible business models, duplicates — with remediation steps and prevention guidance.",
        },
        { property: "og:title", content: `GBP Suspension Lookup | ${SITE.name}` },
        { property: "og:url", content: url },
        { property: "og:type", content: "website" },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "ItemList",
            name: "GBP suspension reasons",
            url,
            itemListElement: SUSPENSIONS.map((s, i) => ({
              "@type": "ListItem",
              position: i + 1,
              name: s.name,
              url: `${url}/${s.slug}`,
            })),
          }),
        },
      ],
    };
  },
  component: SuspensionIndex,
});

function SuspensionIndex() {
  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <main className="mx-auto max-w-4xl px-5 sm:px-8 py-16">
        <nav aria-label="Breadcrumb" className="text-xs uppercase tracking-wider text-muted-foreground">
          <ol className="flex items-center gap-2 flex-wrap">
            <li><Link to="/" className="hover:text-accent">Home</Link></li>
            <li aria-hidden>/</li>
            <li><Link to="/tools" className="hover:text-accent">Tools</Link></li>
            <li aria-hidden>/</li>
            <li>Suspensions</li>
          </ol>
        </nav>
        <header className="mt-6 max-w-3xl">
          <p className="eyebrow">Reference</p>
          <h1 className="headline-display mt-3 text-5xl">GBP suspension reason lookup</h1>
          <p className="mt-4 text-lg text-muted-foreground">
            If your profile vanished or the dashboard says 'Suspended', start here. Each reason has the signals to look for, the fix, and the prevention.
          </p>
        </header>

        <div className="mt-8 flex items-center gap-3 border-t border-b border-border py-4">
          <CiteThis title="GBP Suspension Reason Lookup" url={`${SITE.url}/tools/suspensions`} />
        </div>

        <ul className="mt-10 space-y-4">
          {SUSPENSIONS.map((s) => (
            <li key={s.slug}>
              <Link
                to="/tools/suspensions/$slug"
                params={{ slug: s.slug }}
                className="block rounded-sm border border-border bg-paper-soft p-5 hover:border-foreground hover:bg-background transition-colors"
              >
                <p className="text-xs uppercase tracking-wider text-muted-foreground">
                  {s.severity} suspension
                </p>
                <h2 className="mt-1 text-xl font-semibold">{s.name}</h2>
                <p className="mt-2 text-sm text-foreground/80 line-clamp-2">{s.description}</p>
              </Link>
            </li>
          ))}
        </ul>
      </main>
      <SiteFooter />
    </div>
  );
}
